import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { leadsService } from '../../../../services/leads.service';
import { Button, EmptyState, Skeleton, toast } from '../../../../ui';

interface NoteDto {
  id: number;
  content: string;
  createdAt: string;
  createdBy?: { id: number; name: string } | null;
}

export function NotesTab({ leadId }: { leadId: number }) {
  const qc = useQueryClient();
  const [content, setContent] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['lead-notes', leadId],
    queryFn: async () => {
      const result = await leadsService.getNotes(leadId);
      // Normalize: endpoint returns array directly, but tolerate { items } / { data } too
      if (Array.isArray(result)) {
        return result as NoteDto[];
      }
      return (result?.items ?? result?.data ?? []) as NoteDto[];
    },
    enabled: !!leadId,
  });

  const createMutation = useMutation({
    mutationFn: (note: string) => leadsService.createNote(leadId, note),
    onSuccess: () => {
      setContent('');
      qc.invalidateQueries({ queryKey: ['lead-notes', leadId] });
      toast.success('Nota adicionada');
    },
    onError: () => toast.error('Erro ao salvar nota'),
  });

  function submit() {
    const trimmed = content.trim();
    if (!trimmed) {
      return;
    }
    createMutation.mutate(trimmed);
  }

  return (
    <div className="flex flex-col gap-3">
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="Adicionar nota…"
        className="w-full p-3 rounded-md border border-border bg-surface text-sm text-ink-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        rows={3}
      />
      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={submit}
          disabled={!content.trim() || createMutation.isPending}
          loading={createMutation.isPending}
        >
          Salvar nota
        </Button>
      </div>

      {isLoading ? (
        <Skeleton height={120} />
      ) : !data || data.length === 0 ? (
        <EmptyState title="Sem notas" body="Capture observações sobre este lead." />
      ) : (
        <ol className="flex flex-col gap-2">
          {data.map((n) => (
            <li key={n.id} className="rounded-md border border-border p-3">
              <p className="text-sm text-ink-primary whitespace-pre-wrap">{n.content}</p>
              <p className="text-xs text-ink-tertiary mt-2">
                {new Date(n.createdAt).toLocaleString('pt-BR')}
                {n.createdBy?.name && ` · ${n.createdBy.name}`}
              </p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
